// I'm super over this
/* eslint-disable @typescript-eslint/no-explicit-any */

import {debounce} from 'lodash-es';
import {useCallback, useEffect} from 'react';

import {SimpleTextField} from '../../_old/TextField/SimpleTextField';

import {isObjectSearchParam} from './common';
import {SearchContainer} from './SearchComponents';

import type {
  SearchBarCoreProps,
  SearchParam as InternalSearchParam,
} from './common';

const debouncedResultsHandler = debounce(
  <Searchable extends Record<string, any>>(
    searchValue: string,
    params: Props<Searchable>['params'],
    collection: Props<Searchable>['collection'],
    idProperty: Props<Searchable>['idProperty'],
    handleSearchResults: Props<Searchable>['handleSearchResults']
  ) => {
    const r: Array<{id: string; score: number}> = [];
    const searchLower = searchValue.toLowerCase();

    for (const id of collection.allIds) {
      const item = collection.byId[id];
      let matched = false;
      let bestScore = Infinity;

      for (const param of params) {
        if (isObjectSearchParam(param)) {
          if (param.compare(searchValue, item[param.name])) {
            matched = true;
            bestScore = 0; // Custom compare gets highest priority
            break;
          }
        } else {
          if (typeof item[param] === 'string') {
            const p = (item[param] as string).toLowerCase().trim();
            if (p && searchValue && p.includes(searchLower)) {
              matched = true;
              // Calculate score: exact match (0), starts with (1), contains (index)
              const score = p === searchLower ? 0 : p.startsWith(searchLower) ? 1 : p.indexOf(searchLower) + 100;
              bestScore = Math.min(bestScore, score);
            }
          }
        }
      }

      if (matched) {
        r.push({id: item[idProperty] as string, score: bestScore});
      }
    }

    // Sort by score (lower is better), then alphabetically
    r.sort((a, b) => a.score - b.score || a.id.localeCompare(b.id));

    handleSearchResults(
      r.map((item) => item.id),
      searchValue
    );
  },
  300
);

type Props<T> = SearchBarCoreProps<T> & {
  handleSearchResults: (v: string[], search: string) => void;
  searchValue: string;
  setSearchValue: (value: string) => void;
  shouldClearOnBlur?: boolean;
};
export function SearchBar<Searchable extends Record<string, any>>(
  props: Props<Searchable>
): React.JSX.Element {
  const {collection, idProperty, params, handleSearchResults, setSearchValue} =
    props;

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>): void => {
      const v = e.target.value;
      setSearchValue(v);
      debouncedResultsHandler(
        v,
        // @ts-expect-error don't know proper type, works fine
        params,
        collection,
        idProperty,
        handleSearchResults
      );
    },
    [collection, handleSearchResults, idProperty, params, setSearchValue]
  );

  useEffect(() => {
    return () => {
      debouncedResultsHandler.cancel();
    };
  }, []);

  return (
    <SearchContainer
      className={props.className}
      onBlur={() => {
        if (props.shouldClearOnBlur) {
          props.setSearchValue('');
          handleSearchResults([], '');
        }
      }}
    >
      <SimpleTextField
        value={props.searchValue}
        aria-autocomplete="list"
        autoComplete="off"
        {...props.inputProps}
        onChange={handleChange}
        onKeyDown={(e) => {
          e.key === 'Enter' && e.preventDefault();
          props.inputProps?.onKeyDown?.(e);
        }}
      >
        {props.label}
      </SimpleTextField>

      {props.children}
    </SearchContainer>
  );
}

export type SearchParam<T> = InternalSearchParam<T>;
